<template lang="pug">
    span(
        class="_field",
        :class="_class",
        :style="_style",
        v-on="__fwd")
        div(v-if="$slots.start", class="_flds")
            slot(name="start")
        span(class="_fldi")
            | {{ _prefix }}
            label(
                v-if="privacy || _placeholder",
                v-text="security || _placeholder",
                :for="_id",
                class="_fldl",
                :class="{ _dock: !security && !empty }")
            component(
                class="_fldin",
                :class="_iclass",
                :is="multiline ? 'textarea' : 'input'",
                :id="_id",
                :name="name",
                :required="required",
                :readonly="readonly",
                :disabled="disabled",
                :inert="inert",
                :autofocus="autofocus",
                :tabindex="inert ? -1 : tabindex",
                @change="change",
                @drop="drop",
                @paste="paste",
                @keydown="keydown",
                @keypress="keypress",
                @input="input",
                @select="_select",
                v-on="__on",
                v-bind="bind",
                ref="_in")
            | {{ _suffix }}
        div(v-if="$slots.default || $slots.end", class="_flde")
            slot
            slot(name="end")
</template>

<script>
    import $app from '../../global/app';
    import $device from '../../global/device';
    import entry from './ib-form-entry.vue';

    const bullet = '•';
    const zwnj = '\u200c';    // zero width non joiner; enforces baseline alignment in absence of other text


    /**
     * A standard text input field that supports validation, v-model, and common styling. HTML input attributes such as
     * `maxlength`, `autocomplete`, `autocorrect`, and `spellcheck` can be used with this component.
     */
    export default {
        name: 'ib-field',
        inheritAttrs: false,

        mixins: [
            entry
        ],

        props: {
            /**
             * Value of field; typically implemented using `v-model`.
             */
            value: {
                type: [String,Number],
                default: undefined
            },
            /**
             * Expected writing system; by default, device setting; 'latn' is supported. This can
             * help ensure the cursor appears on the right in Right to Left languages when entering
             * things like usernames or ticker symbols.
             */
            script: String,
            /**
             * If true, allow multiline input. Typically used with the `height` property.
             */
            multiline: Boolean,
            /**
             * Appearance modifier: `box`, `rounded`, `pill`, `frameless` or `underline`. The
             * default is `underline` on Android, `rounded` on iOS, and `box` on other platforms.
             */
            appearance: String,
            /**
             * A prefix that is always displayed in the input box, but is not editable and is not
             * emitted as value. If present, the label placeholder will not be displayed.
             */
            prefix: String,
            /**
             * A placeholder string displayed when field content is empty.
             */
            placeholder: String,
            /**
             * If true, convert all input to uppercase.
             */
            uppercase: Boolean,
            /**
             * If true, convert all input to lowercase.
             */
            lowercase: Boolean,
            /**
             * If true and [`$app.option.privacyMode`](#/global/app) is true, replace displayed content with bullet placeholders on blur.
             */
            privacy: Boolean,
            /**
             * Field should take focus when made visible.
             */
            autofocus: Boolean,
            /**
             * Field contents should be selected automatically on focus.
             */
            autoselect: Boolean
        },

        data() {
            return {
                security: null,     // Privacy mode placeholder
                hcaret: false,      // Hide caret
                empty: true,
                fvis: false
            }
        },

        computed: {
            bind() {
                return {
                    type:
                        this.multiline ? void 0 : 'text',
                    pattern:
                        this.required ? '.*\\S+.*' : void 0,
                    autocapitalize:
                        this.uppercase ? 'character' :
                        this.lowercase ? 'none' : void 0,
                    ...this.$attrs,
                    'class': void 0,
                    'style': void 0
                };
            },

            _id() {
                return this.id || '_f' + this._uid;
            },

            _class() {
                return {
                    _fo: this.focused,
                    _fhct: this.hcaret,
                    _fvis: this.fvis,
                    _da: this.disabled,
                    _ml: this.multiline,
                    _inv: this.showvalid && !this.valid,
                    _sec: this.privacyMode && !this.empty && !this.focused,
                    _rtl: this.$rtl,
                    ['_fa'+this.align]: this.align,
                    ['_fe'+this.appearance]: this.appearance,
                    ['_fs'+this.script]: this.script,
                    ...this.$attrs['class']
                }
            },

            _style() {
                return {
                    width: this.width,
                    height: this.height,
                    ...this.$attrs.style
                };
            },

            _iclass() {
                return (
                    this.uppercase ? 'uppercase' :
                    this.lowercase ? 'lowercase' : null);
            },

            _placeholder() {
                return this.placeholder;
            },

            _prefix() {
                return this.prefix != null && this.prefix != '' ? this.prefix : zwnj;
            },

            _suffix() {
                return null;
            },

            privacyMode() {
                return this.privacy && $app.option.privacyMode;
            },

            /** Convert stored value to displayed value */
            toDisplay() {
                return this.privacyMode
                    ? value => value == null ? '' : this.focused ? value : value.replace(/./g, bullet)
                    : value => value == null ? '' : value;
            }
        },

        watch: {
            multiline() {
                this.$nextTick(this._sync);
            },
            privacyMode(privacyMode) {
                this.security = (privacyMode && !this.focused)
                    ? this.$refs._in.value.replace(/./g, bullet)
                    : null;
            }
        },

        mounted() {
            if (this.autofocus && $device.keyboard) {
                this.$refs._in.focus();
            }
            if (this.privacyMode && !this.focused) {
                this.security = this.$refs._in.value.replace(/./g, bullet);
            }
        },

        methods: {
            /** Set selection range */
            select(start = 0, end = 2E9) {
                const input = this.$refs._in;
                input.selectionStart = start;
                input.selectionEnd = end;
            },

            change() {
                /**
                 * Emitted when focus shifts away from this field after a change in value.
                 * @event change
                 * @type {string}
                 */
                this.$emit('change', this.toValue());
            },

            input() {
                /**
                 * Emitted at any change in value due to user input.
                 * @event input
                 * @type {string}
                 */
                this.hcaret = false;
                this.empty = !this.$refs._in.value;
                this.validate();
                this._emit(this.toValue());
            },

            keydown(e) {
                this.$emit('keydown', e);
                //TODO:Remove, listener should use ib-form
                if (e.which == 13) {
                    this.$emit('enter', e);
                }
            },

            /** @private */
            _sync() {
                // value was changed programmatically
                const value = this.ivalue;
                const input = this.$refs._in;
                if (!Object.is(value, this.toValue())) {
                    const ival = this.toDisplay(value);
                    input.value = ival;
                    this.empty = ival === '' || ival == null;
                    this.validate();
                }
                if (this.privacyMode && !this.focused) {
                    this.security = input.value.replace(/./g, bullet);
                }
            },

            /** @private */
            _selectall(e) {
                const input = this.$refs._in;
                const length = input.value.length;
                if (length) {
                    if (e.relatedTarget == null) {
                        this.dselect = true;
                    }
                    if ($device.ios) {
                        this.hcaret = true;     // do not allow iOS to display selection handles on select all
                    }
                    setTimeout(() => {
                        this.select(0, length);
                    });
                }
            },

            /** @private */
            _selectoff() {
                this.dselect = false;
            },

            /** @private */
            _focus(e) {
                if (this.enter) {
                    this.enter();
                }
                if (this.autoselect && !this.readonly) {
                    this._selectall(e);
                }
                if (this.privacyMode) {
                    this.security = null;
                }
                this.fvis = e.timeStamp < __app.tabts;
            },

            /** @private */
            _blur() {
                this.hcaret = false;
                this.fvis = false;
                this._selectoff();
                if (this.leave) {
                    this.leave();
                }
                if (this.privacyMode) {
                    this.security = this.$refs._in.value.replace(/./g, bullet);
                }
            },

            /** @private */
            _mousedown(e) {
            },

            /** @private */
            _mouseup(e) {
                if (this.dselect) {
                    this.dselect = false;
                    e.preventDefault();
                }
            },

            /** @private */
            drop(e) {
                this.$emit('drop', e);
            },

            /** @private */
            paste(e) {
                this.$emit('paste', e);
            },

            /** @private */
            keypress(e) {
                this.$emit('keypress', e);
            },

            /** @private */
            _select() {
                if (this.inert) {
                    this.select(0, 0);
                }
            },

            /** @private */
            _warn() {
                if (this.warnto) {
                    this._warnend();
                    void this.$el.offsetWidth;
                }
                if (navigator.vibrate) {
                    navigator.vibrate(100);
                }
                this.$el.classList.add('_warn');
                this.$el.addEventListener('animationend', this._warnend);
            },

            /** @private */
            _warnend() {
                this.$el.classList.remove('_warn');
                this.$el.removeEventListener('animationend', this._warnend);
            },

            /**
             * Convert edited/displayed value to `this.value`.
             */
            toValue() {
                let value = this.$refs._in.value.trim();
                if (this.uppercase) {
                    value = value.toUpperCase();
                }
                if (this.lowercase) {
                    value = value.toLowerCase();
                }
                return value;
            },

            /**
             * Save cursor position and value to restore in case of future invalid input.
             * @private
             */
            saveState() {
                const input = this.$refs._in;
                this.saveStart = input.selectionStart;
                this.saveEnd = input.selectionEnd;
                this.saveValue = input.value;
            },

            /**
             * Restore cursor position and value on invalid input.
             * @private
             */
            restoreState() {
                const input = this.$refs._in;
                if (input.value != this.saveValue) {
                    input.value = this.saveValue;
                    input.selectionStart = this.saveStart;
                    input.selectionEnd = this.saveEnd;
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    /** Outer wrapper */
    ._field {
        position: relative;
        display: inline-flex;
        align-items: baseline;
        .font-size(@fs6);
        font-weight: 400;
        width: 24ch;
        color: @fg;
        background-color: @bg-field;
        cursor: text;
        margin: 2px 3px;  /* TODO: REMOVE */
        .field-default();

        &    { .field-padding(4px);  }
        &.xs { .field-padding(0);    }
        &.sm { .field-padding(2px);  }
        &.lg { .field-padding(8px);  }
        &.xl { .field-padding(16px); }

        /** Multiline */
        &._ml {
            height: 4em;
            ._fldi,
            ._fldl,
            ._flds,
            ._flde {
                align-items: flex-start;
            }
            ._fldin {
                height: 100%;
            }
            &    ._fldin { .field-margin(-4px);  .field-padding(4px);  }
            &.xs ._fldin { .field-margin(0);     .field-padding(0);    }
            &.sm ._fldin { .field-margin(-2px);  .field-padding(2px);  }
            &.lg ._fldin { .field-margin(-8px);  .field-padding(8px);  }
            &.xl ._fldin { .field-margin(-16px); .field-padding(16px); }
        }

        &._warn {
            animation: _field-warn .4s linear;
        }

        /** Privacy mode */
        &._sec {
            ._fldin {
                opacity: 0;
            }
            ._fldl {
                opacity: 1;
                text-overflow: clip;
                & when not (@privacy-spacing = 0) {
                    letter-spacing: @privacy-spacing;
                }
            }
            &.numeric ._fldl {
                & when not (@privacy-spacing-num = 0) {
                    letter-spacing: @privacy-spacing-num;
                    -webkit-padding-start: @privacy-spacing-num / 2;
                    padding-inline-start: @privacy-spacing-num / 2;
                }
            }
        }

        /** Disabled */
        &._da {
            cursor: default;
            opacity: .5;
        }

        /** Keyboard modality focus */
        &._fvis {
            outline-offset: -1px;
        }

        ._tag {
            margin: 1px 0;
        }
    }

    ._febox         { .field-box(); }
    ._ferounded     { .field-rounded(); }
    ._fepill        { .field-pill(); }
    ._feframeless   { .field-frameless(); }
    ._feunderline   { .field-underline(); }

    ._fldi,
    ._flds,
    ._flde {
        display: inline-flex;
        align-items: center;
    }

    /** Inner wrapper */
    ._fldi {
        flex: 1 1 auto;
        position: relative;
        white-space: pre;
        min-width: 0;
        max-width: 100%;
        height: 100%;
    }

    /** Slot wrapper */
    ._flds {
        flex: 0 0 auto;
        align-self: stretch;
        -webkit-margin-start: -@field-inner-padding;
        -webkit-margin-end: calc(~'.25em + 4px');
        margin-inline-end: -@field-inner-padding;
        margin-inline-end: calc(~'.25em + 4px');
        color: @border-field;
    }
    ._flde {
        flex: 0 0 auto;
        align-self: stretch;
        -webkit-margin-start: calc(~'.25em + 4px');
        -webkit-margin-end: -@field-inner-padding;
        margin-inline-start: calc(~'.25em + 4px');
        margin-inline-end: -@field-inner-padding;
        color: @border-field;
    }

    /** Label */
    ._fldl {
        position: absolute;
        max-width: 100%;
        opacity: .5;
        overflow: hidden;
        text-overflow: ellipsis;
        pointer-events: none;
        /** Following are to override unwanted bootstrap styles on <label> */
        & when (@platform = web),
               (@platform = marketing) {
            margin: 0;
            font: inherit;
            color: inherit;
        }
        &._dock {
            visibility: hidden;
        }
    }

    /** Input element */
    ._fldin {
        flex: 1 1 auto;
        width: 100%;
        min-width: 1px;
        max-width: 100%;
        margin: 0;
        padding: 0;
        border: none;
        border-radius: 0;
        font: inherit;
        color: inherit;
        cursor: inherit;
        background-color: transparent;
        box-shadow: none;
        box-sizing: content-box;
        -webkit-appearance: none;
           -moz-appearance: none;
                appearance: none;

        &[type=number],
        &[type=date] {
            -moz-appearance: textfield;
        }
        &:focus {
            outline: none;
        }
        &::selection {
            background: fade(@bg-accent, @selection-opacity);
        }
        &::-moz-selection {
            background: fade(@bg-accent, @selection-opacity);
        }
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none !important;
            margin: 0;
        }
    }
    textarea._fldin {
        resize: none;
    }

    /** Hide caret */
    ._fhct {
        caret-color: fade(@bg-select, 0%);
    }

    /** script = "latn" */
    ._fslatn._rtl {
        ._fldin {
            direction: ltr;
            text-align: right;
        }
    }
    ._fslatn._rtl._astart {
        ._fldin {
            text-align: right;
        }
    }
    ._fslatn._rtl._aend {
        ._fldin {
            text-align: left;
        }
    }

    /** align = "left" */
    ._faleft {
        ._fldi {
            justify-content: left;
        }
        ._fldin {
            text-align: left;
        }
    }

    /** align = "right" */
    ._faright {
        ._fldi {
            justify-content: right;
        }
        ._fldin {
            text-align: right;
        }
    }

    /** align = "start" */
    ._fastart {
        ._fldi {
            justify-content: start;
        }
        ._fldin {
            text-align: start;
        }
    }

    /** align = "center" */
    ._facenter {
        ._fldi {
            justify-content: center;
        }
        ._fldin {
            text-align: center;
        }
    }

    /** align = "end" */
    ._faend {
        ._fldi {
            justify-content: flex-end;
        }
        ._fldin {
            text-align: end;
        }
    }

    @keyframes _field-warn {
        0% {
            background-color: fade(@bg-error, 10%);
        } 20% {
            background-color: transparent;
        } 40% {
            background-color: fade(@bg-error, 10%);
        }
    }

    ::-webkit-search-decoration,
    ::-webkit-search-cancel-button,
    ::-webkit-search-results-button,
    ::-webkit-search-results-decoration {
        -webkit-appearance: none;
    }
    ::-webkit-datetime-edit-fields-wrapper,
    ::-webkit-datetime-edit-month-field,
    ::-webkit-datetime-edit-day-field,
    ::-webkit-datetime-edit-year-field {
        padding: 0;
    }
    ::-webkit-calendar-picker-indicator {
        margin: 0;
        padding: 0;
    }
    ::-webkit-clear-button {
        display: none;
    }
    input:-webkit-autofill {
        -webkit-background-clip: text;
        -webkit-text-fill-color: @fg;
        caret-color: @fg;
    }
    input:-moz-autofill,
    input:-moz-autofill-preview {
        filter: none;
    }
    input[type=date] {
        &::-webkit-inner-spin-button {
            height: 1em;
            margin: 0;
        }
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field(v-model="value", id="default", placeholder="Default", privacy)
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="multiline" name="Multiline">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field(v-model="value", placeholder="multiline", multiline, height="4em")
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="attrs" name="Attributes">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field(v-model="value", id="maxlen8", placeholder="maxlength=8 spellcheck=false", maxlength="8", spellcheck="false")
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="appearance" name="Appearance">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field(v-model="value", id="box", placeholder="box", appearance="box")
            .before-4
                ib-field(v-model="value", id="underline", placeholder="underline", appearance="underline")
            .before-4
                ib-field(v-model="value", id="pill", placeholder="pill", appearance="pill")
            .before-4
                ib-field(v-model="value", id="frameless", placeholder="frameless", appearance="frameless")
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="align" name="Alignment">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field(v-model="value", placeholder="start", align="start")
            .before-4
                ib-field(v-model="value", placeholder="center", align="center")
            .before-4
                ib-field(v-model="value", placeholder="end", align="end")
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="transform" name="Transform">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field(v-model="value", id="uppercase", placeholder="uppercase", uppercase)
            .before-4
                ib-field(v-model="value", id="lowercase", placeholder="lowercase", lowercase)
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>

<ib-example id="attrs" name="Attributes">
    <template lang="pug">
        div
            .after-16 Value: 
                span(id="value") {{value}}
            .before-4
                ib-field(v-model="value", id="autoselect", placeholder="autoselect", autoselect)
            .before-4
                ib-field(v-model="value", id="disabled", placeholder="disabled", disabled)
            .before-4
                ib-field(v-model="value", id="readonly", placeholder="readonly", readonly)
            .before-4
                ib-field(v-model="value", id="inert", placeholder="inert", inert)
    </template>
    <script>
        {
            data() {
                return {
                    value: null
                };
            }
        }
    </script>
</ib-example>
